import React from 'react';

import {statusLabel, statusTone} from '../lib/statusTone.js';

// ============================================================================
// Badge — canonical status badge with a CLOSED variant set · CP0 §A4
// ----------------------------------------------------------------------------
// Replaces the free-color `S.badge(bg, tx)` factory (the mechanism behind
// "badge soup"). A badge means STATUS, never decoration. One per row/line, max.
// For soft signals (overdue date, positive ADG, "low") prefer <StatusText>
// (colored ink) over a badge.
//
//   variant ∈ { ok, warn, danger, info, neutral }
//
// Colors come from the Design Constitution tokens (:root): soft background +
// the Table A ink, which passes 4.5:1 on its soft (Q11). Pill, 11px / 700,
// Title Case (§4 Badges). Pass `status` to take the tone from the one
// lifecycle map (L18, Q22) instead of choosing a variant by hand.
//
// NOTE: program/species color is NOT a badge — it's a dot/accent. Use
// programDotStyle() from src/lib/programColors.js for that.
// ============================================================================

const VARIANTS = {
  ok: {bg: 'var(--ok-soft)', fg: 'var(--ok-ink)'},
  warn: {bg: 'var(--warn-soft)', fg: 'var(--warn-ink)'},
  danger: {bg: 'var(--danger-soft)', fg: 'var(--danger-ink)'},
  info: {bg: 'var(--info-soft)', fg: 'var(--info-ink)'},
  neutral: {bg: 'var(--surface-2)', fg: 'var(--text-2)'},
};

// The variant set is CLOSED (see the header comment), so the prop is the union
// of the palette's own keys rather than a free string. The `|| VARIANTS.neutral`
// fallback below stays as the runtime guard for the JavaScript call sites that
// have not converted yet.
export type BadgeVariant = keyof typeof VARIANTS;

type BadgeProps = {
  variant?: BadgeVariant;
  // A lifecycle word ('Complete', 'in_process'): sets the tone from statusTone()
  // and, without children, the Title Case label.
  status?: string | null;
  children?: React.ReactNode;
  title?: string;
  style?: React.CSSProperties;
  // Data hooks a screen's tests select by (data-*), kept when it moves onto Badge.
  attrs?: Record<`data-${string}`, string>;
};

export default function Badge({variant, status, children, title, style, attrs}: BadgeProps) {
  const tone: BadgeVariant = variant ?? (status != null ? statusTone(status) : 'neutral');
  const palette = VARIANTS[tone] || VARIANTS.neutral;
  return (
    <span
      {...attrs}
      data-badge={tone}
      title={title}
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        background: palette.bg,
        color: palette.fg,
        ...style,
      }}
    >
      {children ?? (status != null ? statusLabel(status) : null)}
    </span>
  );
}

export const BADGE_VARIANTS = Object.keys(VARIANTS);
